import type { DataValues } from "../types/narrative";
import { logWarning } from "./logger";

/**
 * Replaces {{placeholder}} syntax with actual data values
 * Supports nested paths like {{time_range.display}}
 *
 * @param text - Text containing {{placeholder}} patterns
 * @param data - Object containing replacement values
 * @returns Text with placeholders replaced
 */
export function replacePlaceholders(text: string, data: DataValues): string {
  return text.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
    const value = getNestedValue(data, path.trim());

    // Handle missing values gracefully
    if (value === undefined || value === null) {
      logWarning(`Placeholder not found: ${path}`);
      return match; // Return original placeholder if not found
    }

    return String(value);
  });
}

/**
 * Gets a nested value from an object using dot notation
 * Example: getNestedValue(obj, "time_range.display")
 */
function getNestedValue(
  obj: DataValues,
  path: string,
): string | number | boolean | undefined {
  const keys = path.split(".");
  let current: string | number | boolean | DataValues | undefined = obj;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current === "object" && key in current) {
      current = current[key as keyof typeof current];
    } else {
      return undefined;
    }
  }

  if (typeof current === "object" || Array.isArray(current)) return undefined;
  return current as string | number | boolean | undefined;
}

/**
 * Formats bold metrics in text by wrapping them in span tags
 * This preserves the visual emphasis for metrics in the text
 *
 * @param text - Text that may contain metric values
 * @returns Array of text segments with bold markers
 */
export function parseBoldText(
  text: string,
): Array<{ text: string; isBold: boolean }> {
  const segments: Array<{ text: string; isBold: boolean }> = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the bold segment
    if (match.index > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, match.index),
        isBold: false,
      });
    }

    // Add the bold segment
    segments.push({
      text: match[1],
      isBold: true,
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({
      text: text.slice(lastIndex),
      isBold: false,
    });
  }

  return segments.length > 0 ? segments : [{ text, isBold: false }];
}
